import type { UserSearchHit } from '../schemas/users';
import { findLocationInMock } from './locations';
import type { UserUpdate } from '../endpoints/updateUser';
import { MOCK_AGENT_EMAIL, appendTraceLine, formatTraceLine } from './traceLine';

// Last 9 digits — a loose match that works for ES and UK national numbers alike,
// so the lookup tolerates spaces, separators and the country-code prefix. The
// canonical normalization lives client-side (detectPhone) and server-side; the
// mock just needs a loose match.
const nationalDigits = (phone: string): string => phone.replace(/\D/g, '').slice(-9);

// A FACTORY and not a literal, so the fixture can be handed back pristine: two helpers below
// mutate this in place (the baja and the correction), and module state that cannot be restored is
// one test quietly rewriting what every later test in its file reads.
const makeDataset = (): Record<string, UserSearchHit[]> => ({
  // Single match — the happy path (open the «Usuario» tab directly).
  '612348902': [
    {
      id: '330148',
      name: 'Lucía Fernández Castro',
      phone: '+34612348902',
      active: true,
      status: 'activo',
      email: 'lucia.f.castro@gmail.com',
      signupDate: '2023-03-12',
      address: 'C/ Alcalá 482, 3º B · 28027 Madrid',
      locationCode: 'DIZ00',
      comms: 'Email y Push',
      deletionDate: '',
      observations: '',
      // Has Amazon history → the baja modal offers the carrier-exclusion check.
      hasAmazonDeliveries: true,
      accountType: { id: 5, name: 'Cliente' },
    },
  ],
  // Anomaly fixture — one phone resolving to more than one «Usuario» (not expected).
  // The inactive recipient is listed first so the defensive fallback has to reorder
  // (active first) to open the active one — making the ordering observable in tests.
  '678221540': [
    {
      id: '330149',
      name: 'Carlos Ruiz',
      phone: '+34678221540',
      active: false,
      status: 'bloqueado',
      email: 'carlos.ruiz@gmail.com',
      signupDate: '2022-07-01',
      address: 'C/ Mayor 1, 1º · 28013 Madrid',
      locationCode: 'ABC00',
      comms: 'Email',
      deletionDate: '',
      observations: '',
      hasAmazonDeliveries: true,
      accountType: { id: 5, name: 'Cliente' },
    },
    {
      id: '330150',
      name: 'Ana Gómez',
      phone: '+34678221540',
      active: true,
      status: 'activo',
      email: 'ana.gomez@gmail.com',
      signupDate: '2024-01-15',
      address: 'C/ Sol 9 · 28012 Madrid',
      locationCode: 'XYZ00',
      comms: 'Push',
      deletionDate: '',
      observations: '',
      // No Amazon history → the baja modal hides the carrier-exclusion section entirely.
      hasAmazonDeliveries: false,
      accountType: { id: 5, name: 'Cliente' },
    },
  ],
  // Carrier pre-registration — account created with a phone only, so the name is
  // empty (real prod case: the search must still resolve it, tab and ficha fall
  // back to the phone / an em dash).
  '664200792': [
    {
      id: '658484',
      name: '',
      phone: '+34664200792',
      active: true,
      status: 'preregistered',
      email: 'preregistro.sin.nombre@example.com',
      signupDate: '',
      address: '',
      locationCode: '',
      comms: '',
      deletionDate: '',
      observations: '',
      hasAmazonDeliveries: false,
      accountType: { id: 5, name: 'Cliente' },
    },
  ],
  // UK single match — lets the dev server exercise the widened E.164 gate end-to-end
  // (last 9 digits of +44 7911 123456). Keyed like the ES fixtures via nationalDigits.
  '911123456': [
    {
      id: '440210',
      name: 'Oliver Bennett',
      phone: '+447911123456',
      active: true,
      status: 'activo',
      email: 'oliver.bennett@example.co.uk',
      signupDate: '2023-09-04',
      address: '221B Baker Street · London NW1 6XE',
      locationCode: 'LDN00',
      comms: 'Email y Push',
      deletionDate: '',
      observations: '',
      hasAmazonDeliveries: false,
      accountType: { id: 5, name: 'Cliente' },
    },
  ],
  // A courier account on a resident's phone: registered once in the couriers' app, blocked, no
  // building of its own. The ficha must say «Mensajero» above the red «Bloqueado» chip, and serves
  // the unblock. No deliveries or device fixture carries this id, so those two regions show their
  // empty states; its observations carry a note typed by hand in the legacy panel, the kind of
  // trail a live courier account brings with it. Invented data, like every fixture here.
  '655010203': [
    {
      id: '812001',
      name: 'Javier Morales Prieto',
      phone: '+34655010203',
      active: false,
      status: 'bloqueado',
      email: 'javier.morales@example.com',
      signupDate: '2019-05-20',
      address: '',
      locationCode: '',
      comms: 'Push',
      deletionDate: '',
      observations: '26 -09-26 - Mensajero bloqueado por entregas sin validar #00000',
      // Amazon history as a recipient, like most accounts in that situation: the baja modal offers
      // the carrier-exclusion check here too.
      hasAmazonDeliveries: true,
      accountType: { id: 24, name: 'Transportista Courier' },
    },
  ],
  // An ACTIVE courier account, so the block can be exercised: the other courier fixture is already
  // blocked and serves the unblock. Invented data, like every fixture here.
  '655010205': [
    {
      id: '812003',
      name: 'Marcos Iglesias Vidal',
      phone: '+34655010205',
      active: true,
      status: 'activo',
      email: 'marcos.iglesias@example.com',
      signupDate: '2022-11-03',
      address: '',
      locationCode: '',
      comms: 'Push',
      deletionDate: '',
      observations: '14/09/26 - Revisar entregas de AMZ #00000 antes de renovar',
      hasAmazonDeliveries: false,
      accountType: { id: 24, name: 'Transportista Courier' },
    },
  ],
  // An account of any other role: the ficha shows it by the name the database gives the role.
  '655010204': [
    {
      id: '812002',
      name: 'Rosa Jiménez Alba',
      phone: '+34655010204',
      active: true,
      status: 'activo',
      email: 'rosa.jimenez@example.com',
      signupDate: '2021-02-11',
      address: '',
      locationCode: '',
      comms: 'Email',
      deletionDate: '',
      observations: '',
      hasAmazonDeliveries: false,
      accountType: { id: 19, name: 'Portero' },
    },
  ],
});

let DATASET = makeDataset();

// Restores the fixture. Same contract as `resetDeliveryDetailMock` and its siblings: a host test
// that drives a WRITE calls this in `beforeEach` so its neighbours start from the fixtures,
// whatever order they run in.
export function resetUsersMock(): void {
  DATASET = makeDataset();
}

export async function searchUsersByPhoneMock(phone: string): Promise<UserSearchHit[]> {
  // Simulate network latency for a realistic dev experience.
  await new Promise((resolve) => setTimeout(resolve, 100));
  // Fresh copies, like a real response parsed off the wire: callers cache what a search returned,
  // and a later in-place fixture mutation (the baja) must never reach into results already handed
  // out — retro-mutating a caller's cache defeats its change detection and repaints nothing.
  return (DATASET[nationalDigits(phone)] ?? []).map((hit) => ({ ...hit }));
}

// The account behind an id, for the mocks that receive one on the wire and have to show a person.
// Returns the two fields such a caller can render and nothing else: this is not a second read, it
// is the lookup a real backend does for itself. `null` when no fixture carries that id — the caller
// decides what an unknown account means, since the answer differs per contract.
export function findUserInMock(id: number): { name: string; phone: string } | null {
  const wanted = String(id);
  for (const hits of Object.values(DATASET)) {
    for (const hit of hits) {
      if (hit.id === wanted) return { name: hit.name, phone: hit.phone };
    }
  }
  return null;
}

// The delivered actor ref: PANEL-CC:<agent email> inside the canonical «Citibox Helper» line,
// distinguishable in data from the automation (APP-<id>) and the legacy panel (PANEL-<id>).
const CITIBOX_HELPER_LINE =
  'Citibox Helper | Mon Aug 31 2026 10:00:00 GMT+0000 (Coordinated Universal Time)' +
  `|PANEL-CC:${MOCK_AGENT_EMAIL}`;

// A courier account keeps its notes, with the `deleted` line UNDER the Citibox Helper one.
function courierBajaTrail(observations: string, reason: string): string {
  const withHelper = appendTraceLine(observations, CITIBOX_HELPER_LINE);
  return appendTraceLine(withHelper, formatTraceLine('deleted', reason));
}

// The DATASET is keyed by phone digits and stays keyed after the baja: the old phone
// keeps resolving the anonymized hit, which is the read amendment's whole semantics. A repeated
// baja changes nothing: backend writes the `deleted` line only when an account really anonymizes.
export function markUserDeletedInMock(id: string, reason?: string): void {
  for (const hits of Object.values(DATASET)) {
    for (const hit of hits) {
      if (hit.id !== id || hit.status === 'anonimizado') continue;
      // A «Usuario» gets the Citibox Helper line alone: a live one never carried notes to keep.
      hit.observations =
        reason === undefined ? CITIBOX_HELPER_LINE : courierBajaTrail(hit.observations, reason);
      hit.active = false;
      hit.status = 'anonimizado';
      hit.name = 'Usuario anonimizado';
      // Backend-delivered literal (CBX-11261): «anonymized» with a «y».
      hit.email = 'anonymized@anonymized.com';
      hit.phone = '';
      hit.address = '';
      hit.locationCode = '';
      hit.comms = '';
      // The anonymization nulls the signup date as well, so the anonymized ficha shows FECHA DE
      // ALTA as an em dash (the delivered contract corrects the capture, which kept it live).
      hit.signupDate = '';
      hit.deletionDate = '2026-08-31';
    }
  }
}

// The «Bloquear» WRITE applied in place. On a «Usuario» it moves TWO fields and no others, and the
// short list IS the contract rather than an oversight: a block is not a baja, so the account keeps
// its name, phone, email, address, matrícula and comms — enough to recognize the «Usuario» if they
// call back and to defend the decision — and writes no deletion date, which would make the ficha
// render FECHA DE BAJA over someone who is still there. A courier account's block carries a
// reason, so its trace line is appended too. An account already blocked is left alone and gets
// no line: two agents reaching the same account settle on one outcome.
export function markUserBlockedInMock(id: string, reason?: string): void {
  for (const hits of Object.values(DATASET)) {
    for (const hit of hits) {
      if (hit.id !== id || !hit.active) continue;
      hit.active = false;
      hit.status = 'bloqueado';
      if (reason !== undefined) {
        hit.observations = appendTraceLine(hit.observations, formatTraceLine('blocked', reason));
      }
    }
  }
}

// The «Desbloquear» WRITE applied in place: only a BLOCKED account moves, exactly like the gate
// the ficha applies — an anonymized one is refused by backend, and an active one is a no-op that
// writes no line (the two-agent race).
export function markUserUnblockedInMock(id: string, reason: string): void {
  for (const hits of Object.values(DATASET)) {
    for (const hit of hits) {
      if (hit.id !== id || hit.active || hit.status !== 'bloqueado') continue;
      hit.active = true;
      hit.status = 'activo';
      hit.observations = appendTraceLine(hit.observations, formatTraceLine('unblocked', reason));
    }
  }
}

// The «Editar datos» WRITE applied in place, so a follow-up searchUsersByPhoneMock returns the
// corrected hit — the phone key is untouched, because the phone is not editable on that route.
// PARTIAL by the same rule as the wire shape: an absent field means «leave it alone», never
// «clear it».
export function markUserUpdatedInMock(id: string, changes: UserUpdate): void {
  // Resolved ONCE, outside the loop: a location id the fixture does not know resolves to null and
  // the address is then left exactly as it was. Blanking it because a lookup missed would be the
  // mock inventing a destructive effect the real write does not have.
  const location = changes.locationId === undefined ? null : findLocationInMock(changes.locationId);

  for (const hits of Object.values(DATASET)) {
    for (const hit of hits) {
      if (hit.id !== id) continue;
      if (changes.name !== undefined) hit.name = changes.name;
      if (changes.email !== undefined) hit.email = changes.email;
      if (location !== null) {
        hit.address = location.address;
        // The whole reason this helper knows about locations: the panel does not send the matrícula
        // and does not compute it, so without imitating the reassignment here the screen could
        // never show the feature's most surprising effect.
        hit.locationCode = location.locationCode;
      }
    }
  }
}
